import React, { useMemo } from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  DEPLOYMENT_COUNT,
  RANKED_SPECIES_COUNT,
  SpeciesCatalogEntry,
  TIME_SLOTS,
  slotEnd,
} from './data/catalogData';
import {
  Badge,
  CARD_UI,
  DeploymentSpreadStrip,
  formatNumber,
  formatPeak,
  spreadClassMeta,
} from './SpeciesCard';
import { UI } from './chartTheme';

interface SpeciesPatternDialogProps {
  species: SpeciesCatalogEntry | null;
  onClose: () => void;
}

const LABEL_SX = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '1px',
  color: CARD_UI.textMuted,
  textTransform: 'uppercase' as const,
};

const PANEL_SX = {
  border: `1px solid ${CARD_UI.panelBorder}`,
  borderRadius: '10px',
  bgcolor: CARD_UI.panelBg,
  p: 2,
};

const StatTile: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <Box sx={{ ...PANEL_SX, p: 1.75, minWidth: 0 }}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#FFFFFF', mt: 0.75, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
    {note && (
      <Typography sx={{ fontSize: '11px', color: CARD_UI.textMuted, mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={note}>
        {note}
      </Typography>
    )}
  </Box>
);

const buildPatternOptions = (species: SpeciesCatalogEntry): Highcharts.Options => {
  const peakIndex = species.peakTime ? TIME_SLOTS.indexOf(species.peakTime) : -1;
  return {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: 240,
      spacing: [8, 4, 4, 0],
      style: { fontFamily: UI.font },
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    accessibility: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: TIME_SLOTS,
      tickInterval: 4,
      labels: { style: { color: UI.textMuted, fontSize: '10px' } },
      lineColor: UI.hairline,
      tickLength: 0,
    },
    yAxis: {
      title: {
        text: 'Combined occurrences',
        style: { color: UI.textMuted, fontSize: '10px', fontWeight: '600' },
      },
      labels: { style: { color: UI.textMuted, fontSize: '10px' } },
      gridLineColor: UI.hairline,
      allowDecimals: false,
    },
    tooltip: {
      backgroundColor: '#0B0F0D',
      borderColor: '#26332C',
      borderRadius: 8,
      padding: 10,
      style: { color: UI.text, fontSize: '11px' },
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const slot = String(this.x);
        const isPeak = this.point.index === peakIndex;
        return `<span style="font-size:10px;color:#8A8A8A">${slot}–${slotEnd(slot)}${isPeak ? ' · peak' : ''}</span><br/>`
          + `<b>${formatNumber(this.y ?? 0)}</b> occurrences`;
      },
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        borderRadius: 2,
        pointPadding: 0.06,
        groupPadding: 0.02,
        animation: { duration: 300 },
        states: { hover: { brightness: 0.15 }, inactive: { opacity: 1 } },
      },
    },
    series: [
      {
        type: 'column',
        name: species.name,
        data: species.pattern.map((y, i) => ({ y, color: i === peakIndex ? CARD_UI.peak : CARD_UI.accent })),
      },
    ],
  };
};

const SpeciesPatternDialog: React.FC<SpeciesPatternDialogProps> = ({ species, onClose }) => {
  const options = useMemo(() => (species ? buildPatternOptions(species) : null), [species]);

  const measured = useMemo(
    () =>
      (species?.spread ?? [])
        .filter((c) => c.status === 'measured')
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
    [species],
  );
  const withoutData = useMemo(
    () => (species?.spread ?? []).filter((c) => c.status === 'no-activity-data'),
    [species],
  );

  if (!species || !options) return null;

  const spreadMeta = spreadClassMeta(species.spreadClass);
  const maxMeasured = Math.max(1, ...measured.map((c) => c.value ?? 0));
  const notDetected = DEPLOYMENT_COUNT - species.deploymentsDetected;

  return (
    <Dialog
      open={Boolean(species)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0C110E',
          backgroundImage: 'none',
          border: '1px solid #1E2E25',
          borderRadius: '14px',
          color: CARD_UI.text,
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
              <Badge label={spreadMeta.name} color={spreadMeta.color} />
              <Badge label={species.type} />
            </Box>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.2 }}>
              {species.name}
            </Typography>
            <Typography sx={{ ...LABEL_SX, color: CARD_UI.textSecondary, mt: 0.75 }}>
              {species.rank !== null
                ? `Daily occurrence rank ${species.rank} of ${RANKED_SPECIES_COUNT}`
                : 'No daily occurrence data'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: CARD_UI.textSecondary, '&:hover': { color: '#FFFFFF' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Headline stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          <StatTile
            label="Daily occurrence"
            value={species.dailyOccurrence !== null ? formatNumber(species.dailyOccurrence) : '—'}
            note={species.share !== null ? `${(species.share * 100).toFixed(1)}% of catalogue total` : 'No activity data'}
          />
          <StatTile
            label="Deployments"
            value={`${species.deploymentsDetected}/${DEPLOYMENT_COUNT}`}
            note={`${Math.round(species.occupancy * 100)}% occupancy`}
          />
          <StatTile
            label="Peak time"
            value={formatPeak(species)}
            note={species.peakTime ? `${species.peakTime}–${slotEnd(species.peakTime)} slot` : undefined}
          />
          <StatTile label="Top pairing" value={species.topPairingSpecies ?? '—'} note="Most often detected alongside" />
        </Box>

        {/* Daily pattern chart */}
        <Box sx={PANEL_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={LABEL_SX}>Daily pattern · 30-minute slots</Typography>
            <Typography sx={LABEL_SX}>
              {species.deploymentsWithActivityData} deployment{species.deploymentsWithActivityData === 1 ? '' : 's'} with activity data
            </Typography>
          </Box>
          {species.patternMax > 0 ? (
            <HighchartsReact highcharts={Highcharts} options={options} />
          ) : (
            <Typography sx={{ fontSize: '12px', color: CARD_UI.textMuted, py: 4, textAlign: 'center' }}>
              This species was only detected in deployments without activity data, so no daily pattern is available.
            </Typography>
          )}
        </Box>

        {/* Deployment spread */}
        <Box sx={PANEL_SX}>
          <Typography sx={{ ...LABEL_SX, mb: 1.5 }}>Deployment spread</Typography>
          <DeploymentSpreadStrip cells={species.spread} height={18} />
          <Box sx={{ display: 'flex', gap: 2.5, mt: 1.25, flexWrap: 'wrap' }}>
            {[
              { label: `Avg. daily occurrence (${measured.length})`, sx: { bgcolor: CARD_UI.accent } },
              { label: `Detected, no activity data (${withoutData.length})`, sx: { border: `1px solid ${CARD_UI.accent}99` } },
              { label: `Not detected (${notDetected})`, sx: { bgcolor: CARD_UI.emptyCell } },
            ].map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', boxSizing: 'border-box', ...item.sx }} />
                <Typography sx={{ fontSize: '11px', color: CARD_UI.textSecondary }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>

          {measured.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2.5 }}>
              {measured.map((cell) => (
                <Box key={cell.deployment} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '12px', color: CARD_UI.text, width: '96px', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {cell.deployment}
                  </Typography>
                  <Box sx={{ flex: 1, height: '6px', borderRadius: '999px', bgcolor: CARD_UI.track, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${Math.max(2, ((cell.value ?? 0) / maxMeasured) * 100)}%`,
                        bgcolor: CARD_UI.accent,
                        borderRadius: '999px',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: CARD_UI.text, minWidth: '56px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(cell.value ?? 0)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {withoutData.length > 0 && (
            <Typography sx={{ fontSize: '11px', color: CARD_UI.textMuted, mt: 2, lineHeight: 1.5 }}>
              Also detected in {withoutData.map((c) => c.deployment).join(', ')},{' '}
              with analysis in progress 
            </Typography>
          )}
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default SpeciesPatternDialog;
